import * as React from 'react';
import {useSize} from '@packages/utilities/styled';

import type {
  HTMLAttributes,
  SizeContextSize,
  SpacingNumber,
  SpringTooling,
} from '../__SharedTypes';
import {renderElement} from '../../utils';
import {type SpringIconType} from '../../icons/Icon';
import {Spinner} from '../Spinner';

import {EmptyStateView, IconWrapper} from './EmptyState.styles';

export type EmptyStateProps = HTMLAttributes & {
  /**
   * Content for `EmptyState`.
   */
  children?: React.ReactNode;
  /**
   * Changes the density and direction of the component.
   */
  density?: 'comfortable' | 'compact';
  /**
   * Renders an icon in the `EmptyState` component.
   */
  icon?:
    | SpringIconType
    | React.ReactElement<React.ComponentProps<SpringIconType>>
    | React.ReactElement<React.ComponentProps<typeof Spinner>>
    | null;
  /**
   * Activates branded styling (WF Visual Sans, fixed typography that ignores
   * SizeContext). Also propagates `isBranded` to the nested `EmptyStateButton`.
   */
  isBranded?: boolean;
  /**
   * Disables the component, including any included `EmptyStateButton` or `EmptyStateLink` components.
   */
  isDisabled?: boolean;
  /**
   * Adds a drop target style to the `EmptyState` component.
   */
  isDropTarget?: boolean;
  /**
   * Makes the `EmptyState` background transparent.
   */
  isTransparent?: boolean;
  /**
   * Changes the size of the component. Defaults to whichever size context the `EmptyState` is nested beneath.
   */
  size?: 'medium' | 'x-large';
  /**
   * The title of the `EmptyState`.
   */
  title?: string;
} & SpringTooling;

const getInnerSpacing = (size: EmptyStateProps['size']) => {
  switch (size) {
    case 'medium': {
      return 1;
    }
    case 'x-large':
    default: {
      return 2;
    }
  }
};

type EmptyStateContextType = {
  isBranded: boolean;
  isCompact: boolean;
  isDisabled: boolean;
  innerSpacing: SpacingNumber;
  size: SizeContextSize;
  title?: string;
};

/**
 * We use Context to coordinate EmptyState with its Actions.
 * This allows us to be opinionated about certain things (such as button/link variants)
 * It also allows us to imperatively disable buttons when the EmptyState is disabled.
 */
const EmptyStateContext = React.createContext<EmptyStateContextType>({
  isBranded: false,
  isCompact: false,
  isDisabled: false,
  innerSpacing: 0,
  size: 'x-large',
  title: undefined,
});

export const useEmptyState = () => React.useContext(EmptyStateContext);

/**
 * # EmptyState
 *
 * Short, descriptive summary here.
 *
 * ## Usage
 *
 * ```jsx
 * import {EmptyState} from '@packages/systems/spring';
 * ```
 *
 * @component
 */
export const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(
  (
    {
      children,
      icon,
      density,
      isBranded = false,
      isDropTarget,
      isDisabled = false,
      isTransparent = false,
      size: sizeProp,
      title,
      ...props
    }: EmptyStateProps,
    ref
  ) => {
    const size = useSize(sizeProp);

    const isCompact = density === 'compact';
    const iconSize = 64;
    const direction = isCompact ? 'row' : 'column';
    const innerSpacing = getInnerSpacing(size);

    return (
      <EmptyStateView
        {...props}
        align="center"
        aria-disabled={isDisabled}
        direction={direction}
        isBranded={isBranded}
        isDisabled={isDisabled}
        isDropTarget={isDropTarget}
        isTransparent={isTransparent}
        ref={ref}
        size={size}
      >
        <EmptyStateContext.Provider
          value={{isBranded, isCompact, isDisabled, innerSpacing, size, title}}
        >
          {icon ? (
            <IconWrapper>{renderElement(icon, {size: iconSize})}</IconWrapper>
          ) : null}

          {children}
        </EmptyStateContext.Provider>
      </EmptyStateView>
    );
  }
);

EmptyState.displayName = 'EmptyState';
EmptyState.toString = EmptyStateView.toString;

export default EmptyState;
